import React from 'react'
import { CustomPicker } from 'react-color'
import type { EditorColoreApiType } from '@utils/types'
import styles from './GithubColorPicker.module.sass'

type TrianglePosition = 'hide' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
type GithubColorItem = string | EditorColoreApiType

type Props = {
  width?: number | string
  colors?: GithubColorItem[]
  triangle?: TrianglePosition
  className?: string
  onChange?: (color: any, event?: any) => void
  onSwatchHover?: (color: any, event?: any) => void
}

const getColorHex = (color: GithubColorItem) => (typeof color === 'string' ? color : color?.hex || '')
const getColorName = (color: GithubColorItem) => (typeof color === 'string' ? color : color?.nome)

const triangleClassByType: Record<TrianglePosition, string> = {
  'hide': styles['triangle--hide'],
  'top-left': styles['triangle--top-left'],
  'top-right': styles['triangle--top-right'],
  'bottom-left': styles['triangle--bottom-left'],
  'bottom-right': styles['triangle--bottom-right'],
}

const GithubColorPicker = ({
  width = 200,
  colors = [],
  onChange,
  onSwatchHover,
  triangle = 'top-left',
  className = '',
}: Props) => {
  const handleSwatchClick = (hex: string, event: React.MouseEvent<HTMLButtonElement>) => {
    onChange?.({ hex, source: 'hex' }, event)
  }

  const handleSwatchHover = (hex: string, event: React.MouseEvent<HTMLButtonElement>) => {
    onSwatchHover?.({ hex, source: 'hex' }, event)
  }

  const handleSwatchKeyDown = (hex: string, event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onChange?.({ hex, source: 'hex' }, event)
  }

  const triangleClass = triangleClassByType[triangle]

  return (
    <div style={{ width }} className={`github-picker ${styles.root} ${className || ''}`}>
      <div className={`${styles.triangleShadow} ${triangleClass}`} />
      <div className={`${styles.triangle} ${triangleClass}`} />
      {colors.map((color, index) => {
        const hex = getColorHex(color)
        if (!hex) return null
        const name = getColorName(color)
        const swatchTitle = name || hex
        const swatchKey = `${hex}_${name || ''}_${index}`

        return (
          <div className={styles.swatchWrap} key={swatchKey}>
            <button
              type="button"
              className={styles.swatch}
              style={{ '--swatch-color': hex } as React.CSSProperties}
              onClick={(event) => handleSwatchClick(hex, event)}
              onMouseOver={(event) => handleSwatchHover(hex, event)}
              onKeyDown={(event) => handleSwatchKeyDown(hex, event)}
              title={swatchTitle}
            />
          </div>
        )
      })}
    </div>
  )
}

export default CustomPicker(GithubColorPicker)
